<template>
  <Teleport to="body">
    <div v-if="!hidden">
      <div
        v-if="!disabled"
        :id="localId"
        ref="tooltipRef"
        :class="computedClass"
        role="tooltip"
      >
        <div
          ref="arrowRef"
          class="arrow"
        />
        <div class="popover-header">
          <slot name="title">
            {{ title }}
          </slot>
        </div>

        <div
          class="popover-body"
          :class="{ hidden }"
        >
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { computePosition, offset, arrow, size, flip, shift, hide } from '@floating-ui/vue'
import { ReversedSide } from './VBTooltip'
import { useListenOnRoot } from './useListenOnRoot'
const ROOT_ACTION_EVENT_SHOW = 'global-open-mz-popover'
/**
 * Find appropriate parent.
 */
const getScrollParent = (el) => {
  if (!el) {
    return null
  }
  if (el.scrollHeight > el.clientHeight) {
    return el
  } else {
    return getScrollParent(el.parentNode) || el
  }
}

const isMouseInRect = (event, rect) => {
  const buffer = 20
  const x = event.clientX
  const y = event.clientY
  rect = {
    top: rect.top - buffer,
    left: rect.left - buffer,
    right: rect.right + buffer,
    bottom: rect.bottom + buffer
  }

  if (x < rect.left || x >= rect.right) return false
  if (y < rect.top || y >= rect.bottom) return false
  return true
}

export default {
  name: 'BPopover',
  props: {
    title: {
      type: String,
      default: ''
    },
    trigger: {
      type: String,
      validator: (value) => ['click', 'hover'].includes(value),
      default: null
    },
    placement: {
      type: String,
      default: 'top'
    },
    target: {
      type: String,
      default: undefined
    },
    customClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    delay: {
      type: Object,
      default: () => ({
        show: 0,
        hide: 0
      })
    }
  },
  emits: ['update:show', 'show', 'shown', 'hide', 'hidden'],
  setup() {
    const { listenOnRoot, emitOnRoot } = useListenOnRoot()
    return {
      listenOnRoot,
      emitOnRoot
    }
  },
  data() {
    return {
      targetRef: null,
      tooltipRef: null,
      arrowRef: null,
      hidden: true,
      rect: null,
      localId: null,
      livePlacement: this.placement,
      showDelayFn: null
    }
  },
  computed: {
    computedClass() {
      return [
        'popover',
        'b-popover',
        this.customClass,
        this.livePlacement ? `bs-popover-${this.livePlacement.split('-')[0]}` : ''
      ]
    }
  },
  mounted() {
    this.localId = `mz-popover-${this.$uuid}`
    this.targetElement = document.getElementById(this.target)
    if (this.targetElement) {
      this.addEvents()
    }
  },
  beforeUnmount() {
    if (this.targetElement) {
      this.removeEvents()
    }
  },
  methods: {
    addEvents() {
      this.targetElement.addEventListener('mouseenter', this.handleMouseEnter)
      this.targetElement.addEventListener('mouseleave', this.handleMouseLeave)
      this.targetElement.addEventListener('click', this.handleClick)
      window.addEventListener('click', this.removeOnClickOutside)
      window.addEventListener('resize', this.hide)
      const p = getScrollParent(this.targetElement)
      p.addEventListener('scroll', this.updateTooltip)
      this.listenOnRoot(ROOT_ACTION_EVENT_SHOW, this.rootEvent)
    },
    removeEvents() {
      this.targetElement.removeEventListener('mouseenter', this.handleMouseEnter)
      this.targetElement.removeEventListener('mouseleave', this.handleMouseLeave)
      this.targetElement.removeEventListener('click', this.handleClick)
      window.removeEventListener('resize', this.hide)
      const p = getScrollParent(this.targetElement)
      p.removeEventListener('scroll', this.updateTooltip)
      window.removeEventListener('click', this.removeOnClickOutside)
    },
    rootEvent(e) {
      if (e.detail._id !== this.localId) {
        this.hide()
      }
    },
    async updateTooltip() {
      if (!this.hidden) {
        this.emitOnRoot(ROOT_ACTION_EVENT_SHOW, { _id: this.localId })
      }
      if (this.targetElement && this.$refs.tooltipRef) {

        const { x, y, middlewareData, placement } = await computePosition(this.targetElement, this.$refs.tooltipRef, {
          placement: this.placement,
          middleware: [
            offset(5),
            size({
              apply({ availableWidth, elements, rects }) {
                Object.assign(elements.floating.style, {
                  maxWidth: `${availableWidth}px`,
                  maxHeight: `${rects.floating.height}px`
                })
              }
            }),
            flip({
              fallbackStrategy: 'bestFit'
            }),
            shift({ padding: 0 }),
            arrow({
              element: this.$refs.arrowRef
            }),
            hide()
          ]
        })
        this.$refs.tooltipRef.style.left = `${x}px`
        this.$refs.tooltipRef.style.top = `${y}px`
        if (middlewareData.arrow) {
          const { x: ax, y: ay } = middlewareData.arrow
          const side = placement.split('-')[0]
          this.livePlacement = placement
          Object.assign(this.$refs.arrowRef.style, {
            left: ax !== null ? `${ax - 3}px` : '',
            top: ay !== null ? `${ay}px` : '',
            right: '',
            bottom: '',
            [ReversedSide[side]]: '-8px'
          })
        }
        if (middlewareData.hide) {
          Object.assign(this.$refs.tooltipRef.style, {
            visibility: middlewareData.hide.referenceHidden
              ? 'hidden'
              : 'visible'
          })
        }
        this.rect = this.$refs.tooltipRef.getBoundingClientRect()
      }
    },
    removeOnClickOutside(e) {
      if (!this.hidden && this.rect && !isMouseInRect(e, this.rect, this.livePlacement)) {
        this.hide()
      }
    },
    handleClick(event) {
      event.stopPropagation()
      if (this.trigger === 'click') {
        if (this.hidden) {
          this.show()
        } else {
          const inRect = isMouseInRect(event, this.rect, this.livePlacement)
          if (!inRect || this.targetElement.contains(event.target)) {
            this.hide()
          }
        }
      } else {
        if (this.targetElement.contains(event.target)) {
          if (this.hidden) {
            this.show()
          } else {
            this.hide()
          }
        }
      }
    },
    handleMouseEnter() {
      if (this.hidden && this.trigger === 'hover') {
        this.show()
      }
    },
    handleMouseLeave(event) {
      clearTimeout(this.showDelayFn)
      if (this.trigger === 'hover' && this.rect) {
        const inRect = isMouseInRect(event, this.rect, this.livePlacement)
        if (!inRect) {
          this.hide()
        } else {
          document.addEventListener('mousemove', this.handleMouseLeave)
        }
      }
    },
    show() {
      this.showDelayFn = setTimeout(() => {
        this.$emit('show')
        this.hidden = false
        this.$nextTick(() => {
          this.updateTooltip()
          this.$emit('shown')
        })
      }, this.delay.show ?? 0)

    },
    hide() {
      setTimeout(() => {
        this.$emit('hide')
        document.removeEventListener('mousemove', this.handleMouseLeave)
        this.$nextTick(() => {
          this.rect = null
          this.hidden = true
          this.$emit('hidden')
        })
      }, this.delay.hide ?? 0)
    }
  }
}
</script>

